import type { ReactNode } from 'react';
import { CaretRightIcon, TargetIcon, WarningIcon } from '@phosphor-icons/react';
import { Skeleton } from '@/components/ui/skeleton';
import type { useSpaceBudget } from '@/hooks/queries/useSpaceBudget';
import type { useSpaceCommitments } from '@/hooks/queries/useSpaceCommitments';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useSpaceActionsHidden } from '@/hooks/useSpacePartnerPreview';
import { useUtcDay } from '@/hooks/useUtcDay';
import { isBudget } from '@/lib/spaces/budget';
import { isCommitments } from '@/lib/spaces/commitments';
import { dayMonthLabel, daysLeft, homeHeroFigures, homeHeroTitle, periodBounds, periodDays, readFailed, type HomeHeroFigures, type PeriodDay } from '@/lib/spaces/home';
import { cn } from '@/lib/utils';
import type { SpaceCommitment, SpaceDetail } from '@/types/spaces';
import { SettlementAvatarGroup, SettlementCard, SettlementFailure, SpaceInfoButton } from './SettlementParts';

type Budget = ReturnType<typeof useSpaceBudget>;
type Commitments = ReturnType<typeof useSpaceCommitments>;
type Format = ReturnType<typeof useFormatCurrency>;

// `commitments` is null for a trip: recurring commitments are monthly.
export function SpaceHomeHero({ space, period, budget, commitments, onBudget }: Readonly<{
  space: SpaceDetail; period: string; budget: Budget; commitments: Commitments | null; onBudget: () => void;
}>) {
  const format = useFormatCurrency();
  const today = useUtcDay();
  const previewing = useSpaceActionsHidden();
  const budgetFailed = readFailed(budget, isBudget);
  const seriesFailed = commitments !== null && readFailed(commitments, isCommitments);
  const limited = Boolean(!budgetFailed && budget.data?.limitCents);
  const title = homeHeroTitle(space, limited, period);
  const retry = () => Promise.all([budget.mutate(), commitments?.mutate()]);
  // A figure missing one of its parts would be a wrong number, not a smaller one: the hero waits for both reads.
  if (budgetFailed || seriesFailed) return <SettlementCard title={title}><SettlementFailure message="Não foi possível carregar o orçamento do espaço." retry={retry} /></SettlementCard>;
  if (!budget.data || (commitments && !commitments.data)) return <div aria-busy="true" aria-label={`Carregando ${title.toLowerCase()}`}><Skeleton className="h-56 rounded-3xl sm:h-64" /></div>;
  const series = commitments?.data?.commitments ?? [];
  const figures = homeHeroFigures(budget.data, series);
  const bounds = periodBounds(space, period);
  const value = figures.availableCents ?? figures.spentCents;
  const over = value < 0;
  // `canEdit` is the API's word; a partner preview puts the viewer's own actions away, so it offers none.
  const define = !figures.limitCents && budget.data.canEdit && !previewing;
  return <section data-brand-fill aria-label={title} className="relative flex h-full flex-col overflow-hidden rounded-3xl bg-primary p-5 text-primary-foreground shadow-md sm:p-7">
    <HeroGlow />
    <header className="relative flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-1.5">
        <h2 className="min-w-0 font-mono text-2xs font-medium uppercase tracking-widest text-primary-foreground/70">{title}{bounds ? <span className="whitespace-nowrap"> · até {dayMonthLabel(bounds.end)}</span> : null}</h2>
        <SpaceInfoButton label={limited ? 'Como o disponível é calculado' : 'O que este número soma'} tone="text-primary-foreground/70 hover:text-primary-foreground"><HeroInfo figures={figures} format={format} /></SpaceInfoButton>
      </div>
      <button type="button" onClick={onBudget} className="hidden shrink-0 cursor-pointer items-center gap-0.5 text-xs font-medium text-primary-foreground/70 transition-colors hover:text-primary-foreground sm:flex">
        {define ? 'Definir orçamento' : 'Orçamento'}<CaretRightIcon aria-hidden weight="bold" className="size-3" />
      </button>
    </header>
    <p className="relative mt-2.5 flex flex-wrap items-center gap-3 font-mono text-5xl leading-none font-semibold tracking-tight tabular-nums sm:mt-3.5 sm:text-6xl">
      {over ? `−${format(-value / 100, { noCents: true })}` : format(value / 100, { noCents: true })}
      <OverPill visible={over} />
    </p>
    <div className="relative mt-2.5 flex items-center gap-2"><SettlementAvatarGroup space={space} max={4} /><span className="text-2xs text-primary-foreground/80">{space.members.length} {space.members.length === 1 ? 'pessoa' : 'pessoas'} · {splitLabel(space)}</span></div>
    <HeroChips figures={figures} format={format} />
    <HeroStrip space={space} period={period} today={today} series={series} end={bounds?.end ?? null} />
    <HeroFooter space={space} figures={figures} end={bounds?.end ?? null} start={bounds?.start ?? null} today={today} format={format} />
    <button type="button" onClick={onBudget} className="relative mt-4 flex cursor-pointer items-center gap-0.5 self-start text-xs font-medium text-primary-foreground/80 hover:text-primary-foreground sm:hidden">
      {define ? 'Definir orçamento' : 'Ver orçamento'}<CaretRightIcon aria-hidden weight="bold" className="size-3" />
    </button>
  </section>;
}

function HeroGlow() {
  return <>
    <span aria-hidden className="pointer-events-none absolute -top-17.5 -right-12.5 size-57.5 rounded-full bg-radial from-primary-foreground/15 to-transparent to-70% sm:size-70" />
    <span aria-hidden className="pointer-events-none absolute -bottom-22.5 -left-10 size-50 rounded-full bg-radial from-primary-foreground/10 to-transparent to-70%" />
  </>;
}

// The fixed bright error, like the personal hero's over days: the role tokens remap per theme and melt into the green.
function OverPill({ visible }: Readonly<{ visible: boolean }>) {
  if (!visible) return null;
  return <span className="rounded-full bg-error-400 px-2 py-0.5 font-sans text-2xs font-bold tracking-normal text-neutral-950">acima do limite</span>;
}

function splitLabel(space: SpaceDetail): string {
  const shares = space.members.map((member) => member.effectivePct);
  if (shares.length > 1 && shares.every((share) => Math.abs(share - shares[0]) < 0.02)) return 'partes iguais';
  return shares.map((share) => share.toLocaleString('pt-BR', { maximumFractionDigits: 2 })).join('/');
}

function HeroChips({ figures, format }: Readonly<{ figures: HomeHeroFigures; format: Format }>) {
  const amount = (cents: number) => format(cents / 100, { noCents: true });
  const chips = figures.limitCents === null
    ? [figures.pendingCents ? `Fixas a vencer ${amount(figures.pendingCents)}` : null]
    : [`Orçamento ${amount(figures.limitCents)}`, `Gasto −${amount(figures.spentCents)}`, figures.pendingCents ? `Fixas a vencer −${amount(figures.pendingCents)}` : null];
  const shown = chips.filter((chip): chip is string => chip !== null);
  if (!shown.length) return null;
  const renderChip = (chip: string) => <span key={chip} className="rounded-lg bg-primary-foreground/15 px-2.5 py-1 text-primary-foreground/90">{chip}</span>;
  return <div className="relative mt-3.5 flex flex-wrap gap-2 font-mono text-2xs tabular-nums">{shown.map(renderChip)}</div>;
}

function HeroInfo({ figures, format }: Readonly<{ figures: HomeHeroFigures; format: Format }>) {
  const amount = (cents: number) => format(cents / 100, { noCents: true });
  if (figures.limitCents === null) return <p>Tudo o que entrou no espaço neste período, de todas as pessoas. Com um limite definido, este número passa a ser o disponível.</p>;
  return <>
    <p className="text-sm font-medium text-text-primary">Como chegamos neste valor</p>
    <dl className="space-y-1">
      <InfoRow label="Limite">{amount(figures.limitCents)}</InfoRow>
      <InfoRow label="Já entrou">−{amount(figures.spentCents)}</InfoRow>
      <InfoRow label="Fixas que ainda vão entrar">−{amount(figures.pendingCents)}</InfoRow>
      <div className="flex items-baseline justify-between gap-3 border-t border-border-subtle pt-1"><dt className="font-medium text-text-primary">Disponível</dt><dd className="font-mono font-semibold text-text-primary tabular-nums">{amount(figures.availableCents ?? 0)}</dd></div>
    </dl>
    <p>Todas as pessoas do espaço veem o mesmo número.</p>
  </>;
}

function InfoRow({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return <div className="flex items-baseline justify-between gap-3"><dt>{label}</dt><dd className="shrink-0 font-mono text-text-primary tabular-nums">{children}</dd></div>;
}

// The strip is a picture of its summary: assistive tech reads the summary instead of a cell per day. A late due day
// takes the fixed bright error, as OverPill does.
function HeroStrip({ space, period, today, series, end }: Readonly<{ space: SpaceDetail; period: string; today: string; series: SpaceCommitment[]; end: string | null }>) {
  const days = periodDays(space, period, today, series);
  if (!days.length || !end) return null;
  const late = series.filter((commitment) => commitment.status === 'overdue').length;
  const upcoming = series.filter((commitment) => commitment.status === 'upcoming').length;
  const trip = space.kind === 'trip';
  const facts = [late ? `${late} ${late === 1 ? 'fixa atrasada' : 'fixas atrasadas'}` : null, upcoming ? `${upcoming} a vencer` : null].filter((fact): fact is string => fact !== null);
  const base = trip ? 'Dias da viagem' : 'Dias do mês';
  const summary = facts.length ? `${base}: ${facts.join(', ')}` : base;
  const renderDay = (day: PeriodDay) => <StripDay key={day.day} day={day} numbered={trip} />;
  return <div className="relative mt-auto hidden pt-4 sm:block">
    <div className="mb-1.5 flex justify-between font-mono text-3xs text-primary-foreground/75"><span>{trip ? 'dias da viagem' : 'mês & fixas'}</span><span>até {dayMonthLabel(end)}</span></div>
    <div role="img" aria-label={summary} className="flex gap-px">{days.map(renderDay)}</div>
    <div aria-hidden className="mt-2.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-3xs text-primary-foreground/75">
      <DueLegend visible={!trip} />
      <Legend className="ring-1 ring-primary-foreground">hoje</Legend>
    </div>
  </div>;
}

function DueLegend({ visible }: Readonly<{ visible: boolean }>) {
  if (!visible) return null;
  return <><Legend className="bg-error-400">fixa atrasada</Legend><Legend className="bg-primary-foreground/60">fixa a vencer</Legend></>;
}

function Legend({ className, children }: Readonly<{ className: string; children: ReactNode }>) {
  return <span className="flex items-center gap-1"><span className={cn('size-2', className)} />{children}</span>;
}

function StripDay({ day, numbered }: Readonly<{ day: PeriodDay; numbered: boolean }>) {
  const cell = day.late ? 'bg-error-400' : day.due.length ? 'bg-primary-foreground/60' : day.phase === 'future' ? 'bg-primary-foreground/15' : 'bg-primary-foreground/35';
  return <div aria-hidden title={[dayMonthLabel(day.day), ...day.due.map((commitment) => commitment.name)].join(' · ')} className="flex min-w-0 flex-1 flex-col items-center justify-end">
    <span className="mb-1 flex h-7 w-full items-end justify-center">{day.due.length ? <DueMarker late={day.late} /> : null}</span>
    <span className={cn('h-2 w-full', cell, day.phase === 'today' && 'ring-1 ring-primary-foreground')} />
    {numbered ? <span className="mt-1 font-mono text-3xs text-primary-foreground/70">{Number(day.day.slice(8, 10))}</span> : null}
  </div>;
}

function DueMarker({ late }: Readonly<{ late: boolean }>) {
  return <span className="flex flex-col items-center"><span className={cn('size-3.5 rounded-full', late ? 'bg-error-400' : 'bg-warning-300')} /><span className="h-1.5 w-px bg-primary-foreground/40" /></span>;
}

// Phones do not get the strip, so they get the number it suggests (the personal hero's daily line).
function HeroFooter({ space, figures, start, end, today, format }: Readonly<{ space: SpaceDetail; figures: HomeHeroFigures; start: string | null; end: string | null; today: string; format: Format }>) {
  if (!start || !end || space.archivedAt || today < start) return null;
  const left = daysLeft(end, today);
  // Days gone by, today included (`daysLeft` counted from the start), never past the period's own length.
  const elapsed = Math.max(1, Math.min(daysLeft(today, start), daysLeft(end, start)));
  let title: string;
  let value: string;
  if (figures.availableCents === null) {
    title = `Média por dia · ${elapsed} ${elapsed === 1 ? 'dia' : 'dias'}`;
    value = `${format(figures.spentCents / 100 / elapsed, { noCents: true })} por dia`;
  } else if (figures.availableCents < 0) {
    title = `Acima do limite · ${left === 1 ? 'último dia' : `restam ${left} dias`}`;
    value = `${format(-figures.availableCents / 100, { noCents: true })} além do limite`;
  } else {
    if (!left) return null;
    title = `Dá para gastar · ${left === 1 ? 'último dia' : `restam ${left} dias`}`;
    value = `${format(figures.availableCents / 100 / left, { noCents: true })} por dia`;
  }
  const Icon = figures.availableCents !== null && figures.availableCents < 0 ? WarningIcon : TargetIcon;
  return <div className="relative mt-4 flex items-center gap-2.5 border-t border-primary-foreground/15 pt-3.5 sm:hidden">
    <span aria-hidden className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-foreground/15"><Icon weight="bold" className="size-4" /></span>
    <div className="min-w-0 flex-1 leading-tight"><p className="text-2xs text-primary-foreground/75">{title}</p><p className="text-sm font-bold">{value}</p></div>
  </div>;
}
